import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, Upload, X, RefreshCw } from 'lucide-react';

const PhotoCapture = ({ currentPhoto, onPhotoCapture, onPhotoRemove }) => {
  const [showCamera, setShowCamera] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // 'user' = frontal, 'environment' = traseira
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  const startCamera = async (facing = facingMode) => {
    try {
      // Parar stream anterior se existir
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: facing,
          width: { ideal: 640 },
          height: { ideal: 480 }
        } 
      });
      setStream(mediaStream);
      setShowCamera(true);
      setFacingMode(facing);
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      }, 100);
    } catch (error) {
      console.error('Erro ao acessar câmera:', error);
      alert('Não foi possível acessar a câmera. Verifique as permissões.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setShowCamera(false);
  };

  const switchCamera = () => {
    const newFacing = facingMode === 'user' ? 'environment' : 'user';
    startCamera(newFacing);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Se for câmera frontal, espelhar a imagem horizontalmente
      if (facingMode === 'user') {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
      }
      
      context.drawImage(video, 0, 0);
      
      const photoData = canvas.toDataURL('image/jpeg', 0.8);
      onPhotoCapture(photoData);
      stopCamera();
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        onPhotoCapture(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    onPhotoRemove();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      {/* Foto atual ou placeholder */}
      <div className="flex justify-center">
        {currentPhoto ? (
          <div className="relative">
            <img 
              src={currentPhoto} 
              alt="Foto" 
              className="w-32 h-32 rounded-full object-cover border-4 border-[#D87A4E]/30"
            />
            <button
              type="button"
              onClick={handleRemovePhoto}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="w-32 h-32 rounded-full bg-slate-200 flex items-center justify-center border-4 border-slate-300">
            <Camera className="h-12 w-12 text-slate-400" />
          </div>
        )}
      </div>

      {/* Câmera ativa */}
      {showCamera && (
        <div className="space-y-3">
          <div className="relative bg-black rounded-lg overflow-hidden">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline
              muted
              className={`w-full h-64 object-cover ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
            />
            {/* Indicador de câmera atual */}
            <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
              {facingMode === 'user' ? '📷 Frontal' : '📷 Traseira'}
            </div>
          </div>
          <div className="flex gap-2 justify-center flex-wrap">
            <Button 
              type="button"
              onClick={capturePhoto} 
              className="bg-green-600 hover:bg-green-700"
            >
              <Camera className="h-4 w-4 mr-2" />
              Capturar
            </Button>
            <Button 
              type="button"
              onClick={switchCamera} 
              variant="outline"
              title="Alternar câmera"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Alternar
            </Button>
            <Button 
              type="button"
              onClick={stopCamera} 
              variant="outline"
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Botões de ação */}
      {!showCamera && (
        <div className="flex flex-wrap gap-2 justify-center">
          <Button 
            type="button"
            onClick={() => startCamera('user')} 
            variant="outline"
            size="sm"
          >
            <Camera className="h-4 w-4 mr-2" />
            Câmera Frontal
          </Button>
          
          <Button 
            type="button"
            onClick={() => startCamera('environment')} 
            variant="outline"
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Câmera Traseira
          </Button>
          
          <Button 
            type="button"
            onClick={() => fileInputRef.current?.click()} 
            variant="outline"
            size="sm"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload
          </Button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};

export default PhotoCapture;
